import React, { useContext, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, Link } from "react-router-dom";
import classnames from "classnames";
import useStyles from "./styles";
import Header from "../Header/Header";
import Sidebar from "components/Sidebar/Sidebar";
import { useLayoutState } from "../../context/LayoutContext";
import { useDispatch, useSelector } from "react-redux";
import { getUserProfileDetails } from "selectors/appSelector";
import { isActivateUserSignIn } from "core/utils/appUtils";
import { externalRoutes, routePaths } from "routes";
import { loadUserDetails } from "actions/login_registrationActions";
import { get } from "lodash";
import PageNotFound from "components/PageNotFound/PageNotFound";
import AppRoleContext, { AppRoleProvider } from "context/AppRoleContext";
import BottomNav from "components/BottomNav/BottomNav";
import { loadAppConfig, loadCompanyConfigAction } from "actions/appActions";
import { fetchBuilderAiCommandsData } from "actions/appBuilderAICommandActions";

import "./AppWrapper.css";
import { isBuilderAdmin, isHomeOwner } from "core/utils/roleUtils";
import { BackdropLoader } from "components/Loader";
import { Typography } from "@mui/material";

export const AppWrapper = () => {
  const dispatch = useDispatch();
  const userProfile = useSelector(getUserProfileDetails);
  const companyId = get(userProfile, "companyId", undefined);

  useEffect(() => {
    if (isActivateUserSignIn()) {
      dispatch(loadUserDetails());
      dispatch(loadAppConfig());
      if (companyId) dispatch(loadCompanyConfigAction({}, "GET"));

      if (userProfile?.applRoleId) {
        dispatch(fetchBuilderAiCommandsData(userProfile.applRoleId));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userProfile?.applRoleId]);

  if (isActivateUserSignIn()) {
    return (
      <>
        <AppRoleProvider>
          <Routes>
            {externalRoutes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={route.component}
              />
            ))}
            <Route path="*" element={<AuthorizedRoutes />} />
          </Routes>
        </AppRoleProvider>
      </>
    );
  } else {
    localStorage.removeItem("token");

    return <Navigate to="/login" replace />;
  }
};

export const AuthorizedRoutes = () => {
  const layoutState = useLayoutState();
  const userProfile = useSelector(getUserProfileDetails);
  const appRoleDetails = useContext(AppRoleContext);

  const classes = useStyles();

  if (!get(userProfile, "userId", undefined)) {
    return (
      <div>
        <Header userProfile={userProfile} />
        <BackdropLoader open={true} />
      </div>
    );
  }

  if (
    isBuilderAdmin(appRoleDetails) &&
    !get(userProfile, "companyId", undefined)
  ) {
    return (
      <div>
        <Header userProfile={userProfile} />
        <div className="mt-5 pt-5">
          <Typography className="h3 text-center mt-5">
            You are not associated with any company. Please add company profile
            to proceed further
          </Typography>
          <Link
            to="/profile/contractor/businessProfile"
            className="text-decoration-none"
          >
            <Typography className="h5 text-center text-primary text-decoration-underline">
              Click here to add Company Details
            </Typography>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={classes.root}>
        <Header userProfile={userProfile} />
        <Sidebar userProfile={userProfile} />
        <div
          className={classnames(classes.content, {
            [classes.contentShift]: layoutState.isSidebarOpened,
          })}
        >
          <div className={classes.fakeToolbar} />
          <div className="app-container" style={{ overflow: "hidden" }}>
            <Routes>
              {routePaths.map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    validateRoute(userProfile, route) ? (
                      <RouteWithCallback route={route} />
                    ) : (
                      <Navigate to={route.fallBack || "/404"} replace />
                    )
                  }
                />
              ))}
              <Route path="/404" element={<PageNotFound />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
          </div>
        </div>

        <BottomNav userProfile={userProfile} />
      </div>
    </div>
  );
};

const validateRoute = (userProfile, route) => {
  const roleName = get(userProfile, "role.applRoleName", "");

  if (route.shouldHide && route.shouldHide(userProfile)) {
    return false;
  }

  if (!route.roles || roleName === "") {
    return true;
  } else if (roleName === "SystemAdmin") {
    return true;
  } else {
    return route.roles.includes(roleName);
  }
};

const RouteWithCallback = ({ route }) => {
  const navigate = useNavigate();

  // Pass navigate or other callback to the route's component
  const onCancel = route.callback ? route.callback(navigate) : null;
  if (route.key === "homeOwnerAddProperty") {
    return React.cloneElement(route.component, { onCancel: onCancel });
  }
  return React.cloneElement(route.component);
};

export default AppWrapper;
